import type { CardStatementFacts, PremierFacts } from '../lib/statements/hsbc.js';
export type { CardStatementFacts, PremierFacts };

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text();
    let code: string | null = null;
    let message = text || res.statusText;
    try {
      const parsed = JSON.parse(text) as { code?: string; message?: string };
      code = parsed.code ?? null;
      if (parsed.message) message = parsed.message;
    } catch {
      // body wasn't JSON — keep raw text as the message
    }
    throw new ApiError(res.status, code, message);
  }
  return (await res.json()) as T;
}

// === Plan 2 additions ===

export interface ParsedLog {
  amountOriginal: number;
  currencyOriginal: 'AED' | 'USD' | 'GBP' | 'EUR' | 'CHF' | 'JPY';
  merchant: string | null;
  description: string | null;
  type: 'expense' | 'income';
  categoryId: string;
  subcategoryId: string | null;
  confidence: number;
}

export interface ParsedLogResponse {
  parsed: ParsedLog & { source: 'merchant_lookup' | 'haiku' };
}

export async function parseInput(input: string): Promise<ParsedLogResponse> {
  return api<ParsedLogResponse>('/api/parse', {
    method: 'POST',
    body: JSON.stringify({ input }),
  });
}

export async function createTransaction(
  parsed: ParsedLog & { confidence: number },
  opts: { fromAccountId: string },
): Promise<{ transactionId: string; fromAccountId: string | null }> {
  const body: Record<string, unknown> = { parsed, from_account_id: opts.fromAccountId };
  return api<{ transactionId: string; fromAccountId: string | null }>('/api/transactions', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface TransferRequest {
  from_account_id: string;
  to_account_id: string;
  amount: number;
  currency: 'AED' | 'USD' | 'GBP' | 'EUR' | 'CHF';
  occurred_at: string;
  description: string;
}

export async function createTransfer(t: TransferRequest): Promise<{ transactionId: string; fromAccountId: string }> {
  return api<{ transactionId: string; fromAccountId: string }>('/api/transactions', {
    method: 'POST',
    body: JSON.stringify({ transfer: t }),
  });
}

export interface RefundRequest {
  refund_of_transaction_id: string;
  from_account_id: string;
  amount: number;
  currency: 'AED' | 'USD' | 'GBP' | 'EUR' | 'CHF';
  occurred_at: string;
}

export async function createRefund(r: RefundRequest): Promise<{ transactionId: string; fromAccountId: string }> {
  return api<{ transactionId: string; fromAccountId: string }>('/api/transactions', {
    method: 'POST',
    body: JSON.stringify({ refund: r }),
  });
}

export interface IncomeRequest {
  category_id: string;
  to_account_id: string;
  amount: number;
  currency: 'AED' | 'USD' | 'GBP' | 'EUR' | 'CHF';
  occurred_at: string;
  merchant?: string | null;
}

export async function createIncome(r: IncomeRequest): Promise<{ transactionId: string; toAccountId: string }> {
  return api<{ transactionId: string; toAccountId: string }>('/api/transactions', {
    method: 'POST',
    body: JSON.stringify({ income: r }),
  });
}

export interface FeedTransaction {
  id: string;
  type: 'expense' | 'income' | 'internal_transfer';
  amountOriginal: number;
  currencyOriginal: string;
  amountAed: number;
  merchant: string | null;
  description: string | null;
  categoryId: string;
  subcategoryId: string | null;
  reviewStatus: 'confirmed' | 'needs_review' | 'auto_high_confidence';
  occurredAt: string;
  refundOfTransactionId: string | null;
}

export async function listTransactions(): Promise<{ transactions: FeedTransaction[] }> {
  return api<{ transactions: FeedTransaction[] }>('/api/transactions');
}

export interface CategoryListItem {
  id: string;
  name: string;
  parentId: string | null;
  kind: 'expense' | 'income';
}

export async function listCategories(): Promise<{ categories: CategoryListItem[] }> {
  return api<{ categories: CategoryListItem[] }>('/api/categories');
}

// === Plan 3 additions ===

export interface MeResponse {
  user: { id: string; displayName: string; createdAt: string } | null;
  lastAutomatedBackupAt: string | null;
  lastAutomatedPriceRefreshAt: string | null;
  default_account_id: string | null;
  default_funding_account_id: string | null;
}

export async function getMe(): Promise<MeResponse> {
  return api<MeResponse>('/api/me');
}

export interface TransactionDetail {
  id: string;
  type: 'expense' | 'income' | 'internal_transfer';
  amount_original: number;
  currency_original: string;
  fx_rate_to_aed: number;
  amount_aed: number;
  category_id: string;
  subcategory_id: string | null;
  category_name: string | null;
  subcategory_name: string | null;
  merchant: string | null;
  description: string | null;
  source: string;
  occurred_at: string;
  created_at: string;
  updated_at: string;
}

export async function getTransaction(id: string): Promise<TransactionDetail> {
  return api<TransactionDetail>(`/api/transactions/${encodeURIComponent(id)}`);
}

export async function patchTransaction(
  id: string,
  patch: Record<string, unknown>,
  opts?: { learnMerchant?: boolean },
): Promise<TransactionDetail> {
  const body: Record<string, unknown> = { ...patch };
  if (opts?.learnMerchant === false) body.learn_merchant = false;
  return api<TransactionDetail>(`/api/transactions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function deleteTransaction(id: string): Promise<void> {
  // Idempotent: 204 success, 404 = already gone (also success).
  const res = await fetch(`/api/transactions/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (res.status !== 204 && res.status !== 404) {
    throw new Error(`delete failed: HTTP ${res.status}`);
  }
}

export async function patchMe(patch: { default_account_id?: string | null }): Promise<MeResponse> {
  return api<MeResponse>('/api/me', {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

export async function setDefaultFundingAccount(accountId: string | null): Promise<{ default_funding_account_id: string | null }> {
  return api<{ default_funding_account_id: string | null }>('/api/me', {
    method: 'PATCH',
    body: JSON.stringify({ default_funding_account_id: accountId }),
  });
}

export interface AccountListItem {
  id: string;
  name: string;
  bank: string;
  type: 'debit' | 'credit' | 'multi_currency_wallet' | 'investment_cash' | 'brokerage';
  kind: 'asset' | 'liability';
  currency_balances: Record<string, number>;
  include_in_networth: boolean;
}

export async function listAccounts(): Promise<{ accounts: AccountListItem[] }> {
  return api<{ accounts: AccountListItem[] }>('/api/accounts');
}

// === Plan 10 drill-down ===

export interface ReportsTransactionRow {
  id: string;
  occurred_at: string;
  day: string;
  amount_aed: number;
  amount_original: number;
  currency_original: 'AED' | 'USD' | 'GBP' | 'EUR' | 'CHF';
  merchant: string | null;
  description: string | null;
  category_id: string;
  subcategory_id: string | null;
  from_account_id: string | null;
  refund_of_transaction_id: string | null;
}

export interface ReportsTransactionsResponse {
  month: string;
  leaf_id: string;
  leaf_name: string;
  parent_name: string | null;
  count: number;
  total_aed: number;
  rows: ReportsTransactionRow[];
}

export async function fetchReportsTransactions(month: string, leafId: string): Promise<ReportsTransactionsResponse> {
  const url = `/api/reports/transactions?month=${encodeURIComponent(month)}&leaf_id=${encodeURIComponent(leafId)}`;
  return api<ReportsTransactionsResponse>(url);
}

// === Control Centre additions ===

export interface CcHeadline {
  cashAvailableAed: number;
  commitmentsDueAed: number;
  confirmedIncomeAed: number;
  unconfirmedIncomeAed: number;
  netAed: number;
  horizonEnd: string;
}

export interface CcWarning {
  kind: 'shortfall' | 'overdue' | 'due_soon' | 'stale';
  message: string;
}

export interface CcDueSoonItem {
  id: string;
  name: string;
  dueDate: string;
  amountNative: number;
  currency: string;
}

export interface CcPositionResponse {
  today: string;
  horizon: number;
  headline: CcHeadline;
  dueSoon: CcDueSoonItem[];
  warnings: CcWarning[];
}

export async function ccGetPosition(horizon: number): Promise<CcPositionResponse> {
  return api<CcPositionResponse>(`/api/control-centre/position?horizon=${horizon}`);
}

export interface CcCardItem {
  commitmentId: string;
  name: string;
  amountNative: number;
  currency: string;
  dueDate: string;
  state: 'overdue' | 'due_soon' | 'upcoming';
  minPayment: number | null;
  occurrenceId: string;
}

export async function ccGetCards(): Promise<{ cards: CcCardItem[] }> {
  return api<{ cards: CcCardItem[] }>('/api/control-centre/cards');
}

export interface DoThisNowAction {
  id: string;
  kind: 'pay_occurrence' | 'refresh_source' | 'check_bank' | 'move_money';
  title: string;
  subtitle: string;
  amountNative: number | null;
  currency: 'AED' | 'USD' | 'GBP' | 'EUR' | 'CHF';
  dueDate: string;
  urgency: 'critical' | 'high' | 'normal';
  occurrenceId: string | null;
  moveMoneyActionId: string | null;
  sourceAccountId: string | null;
  sourceName: string | null;
  confirmationLabel: string;
}

export interface CcActionsResponse {
  actions: DoThisNowAction[];
}

export async function ccGetActions(): Promise<CcActionsResponse> {
  return api<CcActionsResponse>('/api/control-centre/actions');
}

export async function ccConfirmOccurrencePayment(occurrenceId: string, amountNative: number): Promise<void> {
  await api('/api/control-centre/actions', {
    method: 'PATCH',
    body: JSON.stringify({ occurrenceId, action: 'confirm_payment', amountNative }),
  });
}

export async function ccDismissOccurrenceRecommendation(occurrenceId: string): Promise<void> {
  await api('/api/control-centre/actions', {
    method: 'PATCH',
    body: JSON.stringify({ occurrenceId, action: 'dismiss' }),
  });
}

export async function ccAwaitOccurrenceConfirmation(occurrenceId: string): Promise<void> {
  await api('/api/control-centre/actions', {
    method: 'PATCH',
    body: JSON.stringify({ occurrenceId, action: 'awaiting_confirmation' }),
  });
}

export interface CcSnapshot {
  id: string;
  account_id: string;
  balance_native: number;
  currency: string;
  as_of: string;
  source: string;
  created_at: string;
}

export async function ccGetSnapshots(): Promise<{ snapshots: CcSnapshot[] }> {
  return api<{ snapshots: CcSnapshot[] }>('/api/control-centre/snapshot');
}

export async function ccPostSnapshot(body: {
  accountId: string;
  balanceNative: number;
  currency: string;
  asOf: string;
  source: string;
}): Promise<{ snapshot: CcSnapshot }> {
  return api<{ snapshot: CcSnapshot }>('/api/control-centre/snapshot', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface CcAccountConfig {
  accountId: string;
  cashRelevant: boolean;
  displayOrder: number;
}

export interface CcAccountConfigRow {
  account_id: string;
  name: string;
  bank: string;
  type: string;
  kind: string;
  cash_relevant: number;
  display_order: number;
}

export async function ccGetAccountsConfig(): Promise<{ accounts: CcAccountConfigRow[] }> {
  return api<{ accounts: CcAccountConfigRow[] }>('/api/control-centre/accounts-config');
}

export async function ccPutAccountsConfig(
  items: Array<{ accountId: string; cashRelevant: boolean; displayOrder: number }>,
): Promise<{ ok: true }> {
  return api<{ ok: true }>('/api/control-centre/accounts-config', {
    method: 'PUT',
    body: JSON.stringify({ items }),
  });
}

export interface CcCommitment {
  id: string;
  name: string;
  amount: number;
  currency: string;
  direction: 'out' | 'in';
  confirmed: number;           // 0 | 1
  category_id: string | null;
  pay_from_account_id: string | null;
  recurrence: 'one_off' | 'monthly' | 'yearly' | 'weekly';
  anchor_date: string;
  day_of_cycle: number | null;
  active: number;              // 0 | 1
  created_at: string;
}

export interface CcOccurrence {
  id: string;
  commitmentId: string;
  dueDate: string;
  amountAed: number;
  status: 'planned' | 'paid' | 'skipped';
}

export interface CcOccurrenceWithCommitment {
  id: string;
  commitment_id: string;
  due_date: string;
  amount_native: number;
  currency: string;
  status: 'planned' | 'paid' | 'skipped';
  name: string;
  direction: 'out' | 'in';
  confirmed: number;
  category_id: string | null;
  pay_from_account_id: string | null;
}

export async function ccGetCommitments(horizon: number): Promise<{ occurrences: CcOccurrenceWithCommitment[] }> {
  return api<{ occurrences: CcOccurrenceWithCommitment[] }>(
    `/api/control-centre/commitments?horizon=${horizon}`,
  );
}

export async function ccPostCommitment(body: {
  name: string;
  amount: number;
  currency: string;
  direction: 'out' | 'in';
  confirmed?: boolean;
  categoryId?: string | null;
  payFromAccountId?: string | null;
  recurrence: 'one_off' | 'monthly' | 'yearly' | 'weekly';
  anchorDate: string;
  dayOfCycle?: number | null;
  commitmentType?: 'card' | 'other';
  minPayment?: number | null;
}): Promise<{ commitment: CcCommitment }> {
  return api<{ commitment: CcCommitment }>('/api/control-centre/commitments', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function ccPatchOccurrence(
  id: string,
  status: 'planned' | 'paid' | 'skipped',
): Promise<{ ok: true }> {
  return api<{ ok: true }>('/api/control-centre/occurrence', {
    method: 'PATCH',
    body: JSON.stringify({ id, status }),
  });
}

// === Bank SMS ingestion ===
export interface SmsInboxRow {
  id: string;
  status: 'pending' | 'accepted' | 'dismissed';
  msgType: 'purchase' | 'reversal' | 'payment' | 'unsupported';
  bank: string | null;
  cardLast4: string | null;
  accountId: string | null;
  accountName: string | null;
  amountOriginal: number | null;
  currency: string | null;
  merchant: string | null;
  availableLimit: number | null;
  suggestedCategoryId: string | null;
  suggestedSubcategoryId: string | null;
  suggestedCategoryLabel: string | null;
  receivedAt: string;
  transactionId: string | null;
  unknownCard: boolean;
}

export async function smsInbox(status = 'pending'): Promise<{ rows: SmsInboxRow[] }> {
  return api<{ rows: SmsInboxRow[] }>(`/api/sms/inbox?status=${encodeURIComponent(status)}`);
}

export async function smsAccept(
  id: string,
  opts?: { categoryId?: string; subcategoryId?: string | null },
): Promise<{ transactionId: string }> {
  const body: Record<string, unknown> = {};
  if (opts?.categoryId) body.category_id = opts.categoryId;
  if (opts && 'subcategoryId' in opts) body.subcategory_id = opts.subcategoryId ?? null;
  return api<{ transactionId: string }>(`/api/sms/inbox/${id}/accept`, {
    method: 'POST', body: JSON.stringify(body),
  });
}

export async function smsDismiss(id: string): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/api/sms/inbox/${id}/dismiss`, { method: 'POST' });
}

// === HSBC Statement imports ===

export interface OccurrenceUpsertEffect {
  kind: 'occurrence_upsert';
  commitmentId: string;
  dueDate: string;
  statementBalance: number;
  minimumDue: number;
  exists: boolean;
}

export interface SiWarningEffect {
  kind: 'si_warning';
  message: string;
}

export interface SnapshotEffect {
  kind: 'snapshot';
  accountId: string;
  balance: number;
  asOf: string;
  action: 'create' | 'skip_fresher';
  existingAsOf?: string;
}

export interface LoanDriftEffect {
  kind: 'loan_drift';
  bankAmount: number;
  candidates: Array<{ id: string; name: string; amount: number }>;
}

export type StatementEffect = OccurrenceUpsertEffect | SiWarningEffect | SnapshotEffect | LoanDriftEffect;

export interface StatementImport {
  id: string;
  statementType: 'hsbc_card' | 'hsbc_premier';
  statementDate: string;
  fileName: string;
  status: string;
  parsed: CardStatementFacts | PremierFacts | Record<string, never>;
  proposedEffects: StatementEffect[];
}

export async function statementsList(status = 'pending'): Promise<{ imports: StatementImport[] }> {
  return api<{ imports: StatementImport[] }>(`/api/statements?status=${encodeURIComponent(status)}`);
}

export async function statementsAccept(
  id: string,
  opts?: { updateLoanCommitment?: boolean; loanCommitmentId?: string },
): Promise<{ applied: unknown[] }> {
  const body: Record<string, unknown> = {};
  if (opts?.updateLoanCommitment !== undefined) body.update_loan_commitment = opts.updateLoanCommitment;
  if (opts?.loanCommitmentId) body.loan_commitment_id = opts.loanCommitmentId;
  return api<{ applied: unknown[] }>(`/api/statements/${encodeURIComponent(id)}/accept`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function statementsDismiss(id: string): Promise<{ ok: true }> {
  return api<{ ok: true }>(`/api/statements/${encodeURIComponent(id)}/dismiss`, { method: 'POST' });
}
